import NextLink from 'next/link'
import InfiniteScroll from 'react-infinite-scroll-component'
import React from 'react'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text-v1'

import {
  CATEGORY_TYPE_ENUM,
  useAPIDocumentCollectionCategoriesList,
} from 'api/document_collection/useAPIDocumentCollection'
import CategoryError from 'app/DocumentCollectionPage/components/CategoryError'
import DocumentEmptyState from 'app/DocumentCollectionPage/components/DocumentEmptyState'
import {
  CategoryListPreloader,
  CategoryListItemPreloader,
} from 'app/DocumentCollectionPage/components/Preloader'
import { useSETrackEvent } from 'app/DocumentCollectionPage/hooks/useSETrackEvent'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

import type {
  GetCategoryList,
  ResponseSuccessGetDocumentCategoryDetail,
  ResponseSuccessGetDocumentCollection,
} from 'api/document_collection/useAPIDocumentCollection'

interface ILinkProps {
  isExternalLink?: boolean
  href: string
  children: React.ReactNode
  target: string
  onClick: () => void
}

type CategoryListItemProps = {
  parentData?: ResponseSuccessGetDocumentCategoryDetail['data']
}

const PAGINATION_CONFIG = {
  limit: 10,
  offset: 0,
  parentId: '',
}
const DEFAULT_PAGINATION_META = {
  limit: PAGINATION_CONFIG.limit,
  offset: PAGINATION_CONFIG.offset,
  total: 0,
  isNextPageError: false,
}

export const CategoryListItem = (props: CategoryListItemProps) => {
  const { SETrackDocumentLandingPage } = useSETrackEvent()
  const [, setValue, , getValue] = useLocalStorage('categoryLvl', null)

  const { parentData } = props
  const id = parentData?.id
  const categoryLvl = getValue()

  if (id as string) {
    PAGINATION_CONFIG.parentId = id as string
  }

  const {
    isLoading: isListLoading,
    data: documentList,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useAPIDocumentCollectionCategoriesList(PAGINATION_CONFIG)

  const listResult = React.useMemo(() => {
    if (isListLoading) return []
    return documentList?.pages.flatMap((page) => page.data) ?? []
  }, [documentList?.pages, isListLoading])

  const listMeta: ResponseSuccessGetDocumentCollection['meta'] =
    React.useMemo(() => {
      if (isListLoading) return DEFAULT_PAGINATION_META
      return (
        documentList?.pages?.[(documentList?.pages ?? []).length - 1]?.meta ??
        DEFAULT_PAGINATION_META
      )
    }, [documentList?.pages, isListLoading])

  const clickLoadMoreHandler = React.useCallback((): void => {
    const currentPageIndex = (listMeta?.offset ?? 0) / (listMeta?.limit ?? 10)
    refetch({ refetchPage: (page, index) => index === currentPageIndex })
  }, [listMeta, refetch])

  const Link = ({
    isExternalLink = false,
    href,
    children,
    target,
    onClick,
  }: ILinkProps) => {
    return isExternalLink ? (
      <a href={href} target={target} onClick={onClick}>
        {children}
      </a>
    ) : (
      <NextLink href={href} passHref target={target}>
        <a onClick={onClick}>{children}</a>
      </NextLink>
    )
  }

  const setTracker = (category: GetCategoryList, url: string) => {
    let attributes = {}
    let activityType = 'CLICK_KUMPULAN_DOKUMEN_SUBCATEGORY'

    if (parentData?.categoryType === CATEGORY_TYPE_ENUM.CT_MAIN_CATEGORY) {
      attributes = {
        screen_name: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
        event_trigger: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
        category: parentData?.name,
        page_element: category.name,
        url,
      }
    } else if (
      parentData?.categoryType === CATEGORY_TYPE_ENUM.CT_SUB_CATEGORY ||
      parentData?.categoryType === CATEGORY_TYPE_ENUM.CT_UNDEFINED
    ) {
      attributes = {
        screen_name: 'KUMPULAN_DOKUMEN_SUBCATEGORY_PAGE',
        event_trigger: 'KUMPULAN_DOKUMEN_SUBCATEGORY_PAGE',
        parent_category: parentData?.parentName,
        category: parentData?.name,
        category_level: categoryLvl + 1,
        page_element: category.name,
        url,
      }
    }

    SETrackDocumentLandingPage(activityType, attributes)
  }

  if (isListLoading) {
    return (
      <div className="pt-4">
        <CategoryListPreloader />
      </div>
    )
  }

  if (!isListLoading && listResult.length === 0) {
    return <DocumentEmptyState />
  }

  return (
    <InfiniteScroll
      className="min-h-[calc(100vh-350px)] !overflow-hidden"
      dataLength={listResult.length}
      next={fetchNextPage}
      hasMore={hasNextPage}
      loader={
        listMeta?.isNextPageError ? (
          <CategoryError onClick={clickLoadMoreHandler} />
        ) : (
          <CategoryListItemPreloader />
        )
      }
      endMessage={
        listMeta?.isNextPageError && (
          <CategoryError onClick={clickLoadMoreHandler} />
        )
      }
    >
      <ul className="py-4 space-y-4">
        {listResult.map((category) => {
          const url =
            category.categoryType === 'CT_END_CATEGORY'
              ? `/dokumen/daftar/${category.id}`
              : `/dokumen/kategori/${category.id}`

          return (
            <li key={`subcategory-item-${category.id}`}>
              <Link
                href={url}
                target="_self"
                onClick={() => {
                  setValue(categoryLvl + 1)
                  setTracker(category, `${window.location.origin}${url}`)
                }}
              >
                <div
                  key={category.id}
                  className="flex flex-row justify-between p-4 space-x-4 bg-white rounded-lg shadow-md"
                >
                  <div className="flex flex-col grow">
                    <Text variant="heading-sm" className="line-clamp-2">
                      {category.name}
                    </Text>
                    <Text variant="helper" className="mt-1 line-clamp-2">
                      {category.description}
                    </Text>
                  </div>
                  <div className="flex-none">
                    <Icon className="!text-subdued">chevron_right</Icon>
                  </div>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </InfiniteScroll>
  )
}
